import type { TKey } from "@/lib/i18n";
import type { AdParam } from "@/lib/source-types";

/**
 * «Что пишется в клик» (доска SourceEditor), worked out of what the source
 * actually holds. The ad-link template is the one thing that says what a
 * click carries: `sub1={{campaign.id}}` writes the campaign id into sub1. The
 * `tracking_field_map` is something else — other NAMES the edge reads into a
 * sub when a link arrives without it (`utm_placement` → sub4, on an overlay
 * domain only; edgesync portableEntryParams). Facebook's preset names two
 * spellings for sub4 and sub5 (`placement`/`utm_placement`,
 * `campaign_name`/`utm_campaign`); drawn one row per name, they were the
 * table's «sub4 placement · sub4 utm_placement» doubles. So the table is the
 * template's parameters, one row each, and the other names are their own
 * field. Pure.
 */

export interface ClickParam {
  /** The query name: `sub1`, `ttclid`. */
  param: string;
  /** What the platform fills it with: `{{campaign.id}}`. */
  macro: string;
}

const SUB_RE = /^sub([1-9]|1[0-9]|20)$/;

/** The sub's number, or undefined for a name that is not sub1..sub20. */
export function subNumber(param: string): number | undefined {
  const m = SUB_RE.exec(param);
  return m ? Number(m[1]) : undefined;
}

/** The template's query parameters, in the order written. */
export function templateParams(template: string): ClickParam[] {
  const q = template.indexOf("?");
  if (q < 0) return [];
  return template
    .slice(q + 1)
    .split("&")
    .filter(Boolean)
    .map((pair) => {
      const eq = pair.indexOf("=");
      return eq < 0 ? { param: pair, macro: "" } : { param: pair.slice(0, eq), macro: pair.slice(eq + 1) };
    });
}

function withParams(template: string, params: readonly ClickParam[]): string {
  const q = template.indexOf("?");
  const base = q < 0 ? template : template.slice(0, q);
  const query = params.map((p) => (p.macro ? `${p.param}=${p.macro}` : p.param)).join("&");
  return query ? `${base}?${query}` : base;
}

/** The template without the parameter. */
export function removeParam(template: string, param: string): string {
  return withParams(template, templateParams(template).filter((p) => p.param !== param));
}

/**
 * The template with one more of the platform's parameters: under its own sub
 * when that sub is free, else under the first free one. Unchanged when every
 * sub is taken or the macro is already written.
 */
export function addParam(template: string, add: AdParam): string {
  const params = templateParams(template);
  if (params.some((p) => p.macro === add.macro)) return template;
  const taken = new Set(params.map((p) => p.param));
  const param = !taken.has(add.param) ? add.param : Array.from({ length: 20 }, (_, i) => `sub${i + 1}`).find((s) => !taken.has(s));
  return param ? withParams(template, [...params, { param, macro: add.macro }]) : template;
}

/** The platform's parameters the template does not write yet, by meaning — `{campaign_id}` writes «Campaign ID» as much as `{{campaign.id}}` does. */
export function missingParams(template: string, palette: readonly AdParam[], names: Readonly<Record<string, string>>): AdParam[] {
  const written = new Set(templateParams(template).map((r) => paramMeaning(r, palette, names)));
  return palette.filter((p) => !written.has(p));
}

/** A macro's name without its spelling: `{{campaign.id}}`, `{campaign_id}`, `__CAMPAIGN_ID__` → `campaign_id`. */
export function macroName(macro: string): string {
  return macro
    .trim()
    .replace(/^[{_]+|[}_]+$/g, "")
    .replace(/\./g, "_")
    .toLowerCase();
}

/**
 * What a row is in a person's words: the platform's own name for the macro
 * (`{{campaign.id}}` → «Campaign ID», translated by the caller), whatever sub
 * it is written under. Matched by meaning, not by spelling — the live Facebook
 * source writes `sub1={campaign_id}`, the preset `{{campaign.id}}`; both are the
 * campaign id. In order: the macro as the platform writes it; the same name in
 * another spelling (`{placement}` = `{{placement}}`); the preset's own other
 * names (`names` = the platform's tracking-field map: `adgroup_id` → sub2 →
 * TikTok's `__AID__`). A row without a macro is its sub's. Undefined for a
 * macro the platform does not declare.
 */
export function paramMeaning(row: ClickParam, palette: readonly AdParam[], names: Readonly<Record<string, string>>): AdParam | undefined {
  if (!row.macro) return palette.find((p) => p.param === row.param);
  const exact = palette.find((p) => p.macro === row.macro);
  if (exact) return exact;
  const name = macroName(row.macro);
  if (!name) return undefined;
  const spelled = palette.find((p) => macroName(p.macro) === name);
  if (spelled) return spelled;
  const slot = names[name];
  const sub = slot ? slotToSub(slot) : undefined;
  return sub ? palette.find((p) => p.param === sub) : undefined;
}

/** `trackingField4` → `sub4`; anything else as it is. */
export function slotToSub(slot: string): string {
  const n = /^trackingField(\d+)$/.exec(slot)?.[1];
  return n ? `sub${n}` : slot;
}

/** `sub4` → `trackingField4`; undefined for anything but sub1..sub20. */
export function subToSlot(sub: string): string | undefined {
  const n = subNumber(sub);
  return n ? `trackingField${n}` : undefined;
}

/** The stored map as the field's pairs: `utm_placement` = `sub4`, by sub then name. */
export function aliasPairs(map: Readonly<Record<string, string>>): Record<string, string> {
  const n = (slot: string) => Number(/\d+$/.exec(slot)?.[0] ?? 99);
  return Object.fromEntries(
    Object.entries(map)
      .sort(([ka, a], [kb, b]) => n(a) - n(b) || ka.localeCompare(kb))
      .map(([k, slot]) => [k, slotToSub(slot)]),
  );
}

/** The field's pairs back as the stored map; a pair whose value is not sub1..sub20 is dropped (the field refuses it first). */
export function aliasMap(pairs: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(pairs).flatMap(([k, sub]) => {
      const slot = subToSlot(sub.trim());
      return slot ? [[k.trim(), slot]] : [];
    }),
  );
}

/** The platform's names for its macros, in a person's words (the registry speaks English). */
const MEANING: Readonly<Record<string, TKey>> = {
  "Campaign ID": "ops.src.what.campaignId",
  "Ad set ID": "ops.src.what.adSetId",
  "Ad ID": "ops.src.what.adId",
  Placement: "ops.src.what.placement",
  "Campaign name": "ops.src.what.campaignName",
  "Ad set name": "ops.src.what.adSetName",
  "Ad name": "ops.src.what.adName",
  "Site source (fb/ig/an/msg)": "ops.src.what.siteSource",
  "Ad group ID": "ops.src.what.adGroupId",
  "Ad group name": "ops.src.what.adGroupName",
  "Ad (creative) ID": "ops.src.what.creativeId",
  Keyword: "ops.src.what.keyword",
  "Network (g/s/d)": "ops.src.what.network",
  Device: "ops.src.what.device",
  "Click ID": "ops.src.what.clickId",
};

/**
 * A platform parameter's name in a person's words — «Campaign ID» → «ID
 * кампании». The one dictionary for every screen that names one: the
 * source's «Что пишется в клик» and the launched campaign's «Что придёт в
 * отчёты». A name the dictionary lacks is shown as the platform spells it.
 */
export function paramWords(label: string, t: (k: TKey) => string): string {
  const key = MEANING[label];
  return key ? t(key) : label;
}
